/**
 * THE QUEST STEP CATALOG — every step type a quest can be composed from.
 *
 * The split this file enforces: STEP TYPES LIVE IN CODE, QUESTS LIVE IN THE DATABASE. A
 * step has to be measurable — "has she uploaded 12 pieces" is a query against real data —
 * and an admin cannot type a new measurement into a form. So engineers add entries here,
 * and admins compose quests from them: pick steps, order them, weight them, override the
 * copy and the link, target and publish. The same division onboarding flows already use
 * (`api/src/services/onboarding/registry.ts`), for the same reason.
 *
 * Every entry ships complete defaults — label, evidence, why, href, minutes — so a quest
 * that overrides nothing is already presentable. `OVERRIDABLE_FIELDS` names exactly what an
 * admin may change; the measurement (`progress`, `requires`) is never on that list.
 *
 * The catalog was seeded from the Artist Standard prototype's four intent variants, so
 * every step here has already been driven on a real page. The copy rules that bound those
 * steps bind these defaults and every override: the reason lives IN the label, the sell
 * addresses HER, no downward comparisons, no analytics params in an href — all held by
 * `shared/test/quests/score.spec.ts` ("the catalog copy rules") and
 * `shared/test/quests/step-catalog.spec.ts` (where a step is allowed to send her).
 */

import { PROFILE_COMPLETENESS_MAX, profileCompleteness, type CompletenessProfile } from '../profile-completeness';
import { buildSparklinkUrl, SPARKLINK_FIRST_UPLOAD_MESSAGE } from '../sparklink';

/** Where a sparklink lands. The catalog ships absolute links; they open in a new tab. */
const ARTHELPER_ORIGIN = 'https://www.arthelper.com';

/**
 * Spark ids, and the one thing to know about them: they are per-environment. The
 * first-upload email resolves its spark by NAME (`CompletionType.findOne({ name:
 * 'Art Analysis' })`) precisely because the id is not portable, and this catalog
 * cannot do that — it is framework-free and has no database. So every id here has
 * to be confirmed against production before its step goes into a live quest.
 * Verified locally only; 380 predates this file and came from the broadcast itself.
 */
const PRODUCT_DESCRIPTION_SPARK_ID = 1; // local only — unconfirmed in production
const ART_ANALYSIS_SPARK_ID = 26; // local only — unconfirmed in production
// Confirmed in production: these are the ids the live onboarding series links carry.
// NOTE: the 2026-08-10 pricing broadcast uses spark 9 in the community, while the
// `priced` step below still opens the pricing app. Both are done-for-you; switching
// the step to the broadcast's spark is a behaviour change, not a copy one.
const CONTENT_BATCH_SPARK_ID = 50;
const MAGAZINE_FEATURE_SPARK_ID = 5;
const SOLO_SHOW_SPARK_ID = 360;
const RULE_BREAKER_SPARK_ID = 49; // "You break the same rule in every piece" (2026-08-14)
const GALLERY_MATCHES_SPARK_ID = 481; // "I found galleries near you…" (2026-08-07) // "I curated your first solo show" (2026-08-30)
// The best-clicking Studio broadcasts of 2026-09 and 2026-10, confirmed in production
// 2026-11-03. Click rates are clicks over recipients on the full send.
const COLLECTOR_VISIT_SPARK_ID = 88; // "A collector picked your piece" (2026-09-14) — 7.3%
const NEXT_PIECE_SPARK_ID = 501; // "I found the piece you haven't made yet" (2026-09-01) — 5.3%
const ALMOST_BOUGHT_SPARK_ID = 525; // "Someone almost bought your piece" (2026-09-27) — 4.8%
const DOCUMENTARY_SPARK_ID = 511; // "I wrote a documentary about your work" (2026-09-09) — 4.3%
const WIKIPEDIA_SPARK_ID = 508; // "I wrote your Wikipedia page" (2026-09-06) — 4.2%

/**
 * Community destinations the onboarding series already points new members at. The
 * utm parameters those short links carry are deliberately dropped — a quest click
 * is not an email click, and the copy rules forbid analytics params in an href.
 */
/** "I made you a book out of your art" (2026-08-24) — 22.2% open, 3.5% click, the
 * best-clicking broadcast in the last 30 days. Same /surprise-me shape as the review
 * and the pricing links: the book is already made when they arrive. */
const ART_BOOK_URL = 'https://artbooks.arthelper.com/book/recent-work/surprise-me';

/** "I made you a Reel. Wow, it's good." (2026-10-15) — 7.4% click, the best of the Studio
 * broadcasts that month. The autoposter picks a piece, builds the Reel and writes the
 * caption on arrival, so it is already made when they land. */
const REEL_URL = 'https://autoposter.arthelper.com/new-post/surprise-me?post_type=video';

/** "I made you a carousel. Swipe through it." (2026-10-26) — 4.7%. The Reel's sibling. */
const CAROUSEL_URL = 'https://autoposter.arthelper.com/new-post/surprise-me?post_type=swipeable-set';

/** "I looked at your profile the way a collector does" (2026-08-29) — 22.6% / 2.4%. */
const PROFILE_MIRROR_URL = 'https://app.arthelper.ai/profile';

/** "I found 10 artists you'd really click with" (2026-07-20) — every CTA in it is a
 * "+ Connect" on a real artist's profile, so the step lands on the matches, not a tool. */
const ARTIST_MATCHES_URL = 'https://app.arthelper.ai/network';
/** The onboarding wizard — where an unfinished member picks up where she left off. */
const ONBOARDING_URL = 'https://app.arthelper.ai/welcome';
/** Where a member creates a Guild or finds one to join. */
const GUILDS_URL = `${ARTHELPER_ORIGIN}/guilds`;

/** Every contest. The api swaps in the open Open Category round where it can (`contestService.openCategoryHref`). */
const OPEN_CONTEST_URL = '/contests';
const ART_DEBATE_URL =
  '/community/artdebates/post/does-showing-your-process-take-something-away-from-the-finished-work';

/** Art Review is a separate app on its own subdomain; steps that use it open a new tab. */
const ART_REVIEW_HOME = 'https://artreview.arthelper.com';
export const ART_REVIEW_URL = `${ART_REVIEW_HOME}/review-1/surprise-me`;
export const ART_PRICING_URL = `${ART_REVIEW_HOME}/pricing-1/surprise-me`;

/** The room-mockup generator, its own app. Verified live (200). */
export const MOCKUPS_URL = 'https://mockups.arthelper.com/';

/**
 * The Ideal Collector sparklink — spark 380, VERBATIM from the 2026-08-15 broadcast
 * (~76k recipients), so an artist arriving from a quest gets the same Arty conversation as
 * one arriving from the email. Measured via `completions.completionTypeId = 380`.
 */
export const IDEAL_COLLECTOR_SPARK_ID = 380;
export const IDEAL_COLLECTOR_URL =
  'https://www.arthelper.com/community?spark_id=380&message=' +
  'Look%20at%20all%20my%20work%20and%20tell%20me%20exactly%20who%20my%20ideal%20collector%20is%20-%20' +
  'who%20they%20are%2C%20what%20is%20already%20hanging%20on%20their%20walls%2C%20and%20where%20they%20' +
  'are%20most%20likely%20to%20run%20into%20my%20work.';

/** The measured body-of-work bar: the median piece count of ASF sellers (n=13,929). */
export const BODY_OF_WORK_BAR = 12;
/** The aspirational posting cadence, days/week. A goal we set, not a norm we measured. */
export const POSTING_CADENCE_BAR = 3;

/**
 * Everything a step's measurement may read. Every field optional: callers hand over the
 * subset they loaded, and an absent field scores zero rather than throwing.
 */
export type QuestSubject = {
  imageCount?: number | null;
  reviewedCount?: number | null;
  pricedCount?: number | null;
  mockupCount?: number | null;
  connectedChannelCount?: number | null;
  postingDaysPerWeek?: number | null;
  profile?: CompletenessProfile | null;
  /**
   * True once the artist has EDITED her profile at least once (updatedAt moved past
   * createdAt). The bar for the profile step (Nick, 2026-08-31): show a profile update,
   * not a perfect score — the weighted completeness still paints partial credit.
   */
  profileEverUpdated?: boolean | null;
  communityPostCount?: number | null;
  /**
   * How many listed posts she can earn on she has commented on in the window, and the
   * next of those she has not, as `<community>/post/<slug>` under /community/. Her own
   * listed posts are left out of both. `targetPostEarnableCount` is how many she can
   * earn on, and the bar is capped at it. Loaded per bounty, never in a quest.
   */
  targetPostCommentCount?: number | null;
  targetPostPath?: string | null;
  targetPostEarnableCount?: number | null;
  /**
   * Community posts she authored, except the welcome-wizard intro. The one-click
   * critique still counts: that is "Ask for an art critique", not the intro.
   */
  postForCritiqueCount?: number | null;
  /** Community posts of type CRITIQUE she authored — the ask, not any post. */
  critiquePostCount?: number | null;
  commentsGivenCount?: number | null;
  /**
   * Comments left on ANOTHER artist's artwork page — `image_comments`, not
   * `community_comments`. A different table and a different surface from
   * `commentsGivenCount`: this one is the comment box under a piece at
   * /<artist>/image/<piece>, which is where an artist actually meets a stranger's work.
   */
  artworkCommentsGivenCount?: number | null;
  followingCount?: number | null;
  /** Poll votes cast — one tap, nothing written. */
  pollVoteCount?: number | null;
  /** Likes given on ANOTHER artist's artwork — each one notifies its owner. */
  artworkLikesGivenCount?: number | null;
  describedCount?: number | null;
  specsCount?: number | null;
  collectionCount?: number | null;
  /** Ideal Collector runs — `completions.completionTypeId = 380`. */
  idealCollectorCount?: number | null;
  /**
   * Completions per spark, keyed by `completions.completionTypeId`. One grouped
   * query behind every spark step, so adding the next one costs a catalog entry
   * and nothing else — no new subject field, no new subquery.
   */
  sparkCompletionCounts?: Record<string, number> | null;
  /**
   * Connection REQUESTS she sent — not accepted ones, and not either direction.
   *
   * Both this doc and the step's admin description used to say "accepted connections,
   * either direction", which is a measurement the loader deliberately does NOT use: an
   * accepted-only count leaves a finished step sitting at zero while strangers ignore her,
   * and drops back down if one is later removed. Every step must be something she can
   * finish on her own. The admin picker renders that description verbatim, so a wrong one
   * is a quest composed on a false premise.
   */
  connectionsMadeCount?: number | null;
  /**
   * BRINGING LAPSED ARTISTS BACK, five ways, each counted against artists the picker
   * NAMED as lapsed and each counting DISTINCT PEOPLE, not actions.
   *
   * Both halves are load-bearing. "Lapsed" cannot be read back from live data — the
   * last-seen stamps are overwritten — and reading "lapsed right now" would un-finish a
   * member's step at the moment the artist came back, which is the outcome the quest
   * exists to cause. And "bring five people back" means five people: counting rows would
   * let somebody clear the bar working down one artist's gallery.
   */
  reviveLikeCount?: number | null;
  reviveCommentCount?: number | null;
  reviveFollowCount?: number | null;
  reviveConnectCount?: number | null;
  reviveNoteCount?: number | null;
  /** Structured critiques she has WRITTEN. The Valued Critic badge is the payoff. */
  critiquesGivenCount?: number | null;
  /**
   * ANSWERING THE OTHER THREE ASK-FOR-HELP POST TYPES, each on somebody else's piece.
   *
   * Unlike the greet* counts these carry no "was the asker new" test: the ask itself is
   * the invitation, so any artist's post counts. The self-exclusion is the only filter —
   * rating, pricing or voting on your own post is not participation.
   */
  artRatingsGivenCount?: number | null;
  pricingGivenCount?: number | null;
  pollVotesGivenCount?: number | null;
  /** Posts published through the autoposter — the first one is the step that matters. */
  /** Friends who actually joined from her invite (`reason: 'referral'` ledger rows). */
  referralCount?: number | null;
  /**
   * GREETING A NEWCOMER, six ways, each counted against members who were NEW AT THE TIME
   * OF THE GREETING.
   *
   * That "at the time" is load-bearing. Counting against "joined in the last 30 days"
   * instead would make progress DECREASE as the people she greeted age out — a finished
   * step silently un-finishing itself, which is the worst kind of bug to debug from a
   * screenshot. Measuring the gap between the greeting and their signup is stable forever.
   */
  /** Newest public piece, for steps that open a tool ON a specific piece. */
  newestImageId?: string | null;
  greetWelcomeCount?: number | null;
  greetReactCount?: number | null;
  greetArtworkCommentCount?: number | null;
  greetPostCommentCount?: number | null;
  greetFollowCount?: number | null;
  greetConnectCount?: number | null;
  greetCritiqueCount?: number | null;
  /**
   * Newcomer ARTISTS who followed her and whose art she then liked or reacted to — the
   * follow-back loop (#4479). One per artist, whatever she stacks on their pieces.
   */
  greetFollowerReactCount?: number | null;
  /** True once she has finished the onboarding wizard (`users.welcomeCompletedAt`). */
  onboardingCompleted?: boolean | null;
  /**
   * Contest ENTRIES she has made: top-level, approved comments on an active contest's post,
   * not her own, made while it was open. An entry is a comment because a contest post is
   * an admin-made container — members cannot create one.
   */
  contestEntryCount?: number | null;
  /**
   * Guilds she belongs to right now — an active `guild_members` row, which creating one,
   * an approved application and an accepted invite all produce. A pending application is
   * not membership. State, not activity, so a guild she was already in counts.
   */
  guildMembershipCount?: number | null;
};

/**
 * `comment-targets` opens the picker: a few artists chosen for this viewer whose work
 * nobody has commented on yet, each with one piece to open. The step exists because
 * "go comment on someone" is not a task a new artist can act on — they do not know
 * anyone — so the app has to name the person and the piece.
 */
/**
 * Every behaviour a step's tap can have. Declared as a runtime list, with the type
 * derived from it, so the resolver can reject an unknown `opens` override at the
 * boundary — a type-only union would have left that validation to drift.
 *
 * - `dormant-note` — the FOCUSED note composer. The general picker offers five ways to
 *   act on an artist at once, which buried the one control this step needs; this shows a
 *   piece, lines written about it, and a Send, and nothing else.
 * - `artwork-modal` — the add-artwork modal, in place.
 * - `explainer` / `comment-targets` / `like-targets` / `artist-matches` — an in-band panel.
 * - `arty` — hands the step's sparklink to the Arty panel already mounted on the page,
 *   instead of opening www.arthelper.com/community?spark_id=… in a second tab. Same
 *   spark, same opening message, no navigation.
 * - `compose-post` — the community feed with its post composer already open, same tab
 *   (`/community?compose=1`, the deep link the composer reads on arrival).
 * - `new-tab` — genuinely another application on another origin. New tab on desktop, same
 *   tab on a phone, where a second tab is hard to get back from and iOS Safari may refuse
 *   to open one at all.
 */
export const STEP_OPENS_VALUES = [
  'artwork-modal',
  'new-tab',
  'embed',
  'comment-targets',
  'like-targets',
  'arty',
  'artist-matches',
  'invite',
  'critique-post',
  'compose-post',
  'greet-targets',
  'dormant-targets',
  'dormant-note',
  'critique-answer',
  'rating-answer',
  'pricing-answer',
  'poll-answer',
] as const;

export type StepOpens = (typeof STEP_OPENS_VALUES)[number];

export type QuestStepType = {
  code: string;
  /** Admin-facing name in the catalog picker. */
  adminLabel: string;
  /** What the measurement reads, shown in the admin so overrides stay honest. */
  adminMeasurement: string;
  /**
   * The member-facing line. `ctx.target` lets a bounty render its own bar ("2 of 3")
   * on a step whose default bar is different; a quest calls `label(subject)` and gets
   * exactly the string it always has.
   */
  label: (subject: QuestSubject, ctx?: StepLabelContext) => string;
  evidence: string;
  why: string;
  href: string;
  opens: StepOpens;
  /**
   * The button's words where the step is shown as a message with a button — an onboarding
   * series message (#4215). Optional; a message with none reads "Open it".
   */
  cta?: string;
  minutes?: number;
  aspirational?: boolean;
  requires?: (subject: QuestSubject) => boolean;
  /**
   * How this step completes (Nick, 2026-08-31):
   * - 'measured' — derived from real rows on every read; never stored, never uncheckable.
   * - 'click'    — the work happens in another app whose result we cannot reliably read
   *                back (Art Review, pricing, ideal collector, mockups), so clicking
   *                through IS the completion. Stored once, per user per step code.
   * Any step can ALSO be checked off manually by the artist; a stored completion earns
   * the step's full weight. `progress` still paints partial credit under a click step
   * where a measurement exists.
   */
  completion: 'measured' | 'click';
  /**
   * WHAT WINDOW THE MEASUREMENT READS (Nick, 2026-09-25).
   *
   * - 'since-join' — only what she does AFTER the quest starts for her. Every step that
   *   asks her to go and DO something: follow, connect, like, comment, critique, invite,
   *   greet a newcomer. Counting these over her whole history put an established member at
   *   100% the moment she joined — and that first render is also what stamps completion
   *   and pays the reward, so she was paid for work that predated the quest.
   * - 'lifetime' — state she has BUILT, which is what the step means. A portfolio does not
   *   restart because a quest did, and "12 pieces" asks about the gallery, not the week.
   *
   * REQUIRED, not optional, so a new step cannot silently inherit the wrong window — the
   * failure mode is a step that reads complete for everyone, which is invisible until
   * somebody screenshots it. Never admin-editable: it is half of the measurement.
   */
  scope: 'lifetime' | 'since-join';
  /** 0..1. The measurement. Never admin-editable. */
  progress: (subject: QuestSubject) => number;
  /**
   * THE COUNT BEHIND A SIMPLE BAR, declared so a bounty can ask for a different one.
   *
   * Set only on steps whose `progress` is exactly `ratio(subject[metric], bar)` — a spec
   * holds every declaring step to that, so the two can never drift apart. A bounty with a
   * `target` measures `subject[metric] >= target`; one without falls back to
   * `progress(subject) >= 1`, which works for every measured step. Steps that do not
   * declare these (coverage steps, profile fields, sparks) simply do not accept a target.
   */
  metric?: CountMetric;
  bar?: number;
  /** False for local-only spark IDs that must not ship as live measured coverage. */
  productionReady?: boolean;
};

/**
 * The fields an admin may override per quest, and the shape `quest_steps.configOverrides`
 * is validated against. Label overrides are plain strings — a per-quest label loses the
 * live count a function label can render, which is the honest trade for admin editability;
 * leave the label unset to keep the counting default.
 */
export const OVERRIDABLE_FIELDS = ['label', 'evidence', 'why', 'href', 'minutes', 'opens', 'cta', 'measures'] as const;

/**
 * A CUSTOM STEP — the admin's own wording, on machinery that already exists.
 *
 * Step types live in code because a step has to be MEASURABLE and an admin cannot type a
 * measurement into a form. A custom step does not break that: it supplies copy, a link
 * and a click behaviour, and for the measurement it either has none (click-completion,
 * exactly like the steps whose work happens in another app) or BORROWS a real catalog
 * step's via `measures`. Either way no new measurement is invented at the boundary.
 *
 * `quest_steps` is unique on (questId, stepCode), so a quest carrying two custom steps
 * needs two distinct codes — hence the `custom:1`, `custom:2` form rather than a bare
 * `custom`. Everything downstream resolves the prefix; nothing stores a second catalog.
 */
export const CUSTOM_STEP_PREFIX = 'custom:';

export function isCustomStepCode(stepCode: string): boolean {
  return stepCode.startsWith(CUSTOM_STEP_PREFIX);
}

/**
 * A DAY BREAK — not a step, a divider between them.
 *
 * Nick, 2026-09-25: a quest should be able to run across days, so finishing today's steps
 * shows "you have done today's, come back tomorrow" rather than the next day's work. The
 * goal is the return, not the checklist.
 *
 * It rides `quest_steps` as a sentinel row for one reason: the admin editor already drags
 * that list and persists `position`, so a divider costs no migration, no second ordering,
 * and no parallel editor. The `:N` suffix is the `custom:N` trick for the same constraint
 * — `UNIQUE(questId, stepCode)` means four dividers need four distinct codes.
 *
 * Everything that treats a quest_steps row as a step has to skip it: the scorer (which
 * turns dividers into each step's `day` and then drops them), and the weight-total
 * validators, since a divider carries no weight and 100 must still mean 100.
 */
export const DAY_BREAK_STEP_PREFIX = 'day-break:';

export function isDayBreakStepCode(stepCode: string): boolean {
  return stepCode.startsWith(DAY_BREAK_STEP_PREFIX);
}

/**
 * THE TWO PIECES OF DAY-BREAK ARITHMETIC THE EDITOR DOES.
 *
 * Proven in `shared/test/quests/day-break.spec.ts`. A divider counted into the weight
 * total blocks a quest from going live, and a reused code collides with
 * UNIQUE(questId, stepCode) on save.
 */

/** A divider is a sentinel step row, so it needs a code no sibling already holds. */
export function nextDayBreakCode(stepCodes: readonly string[]): string {
  const used = stepCodes
    .map((code) => Number(/^day-break:(\d+)$/.exec(code)?.[1] ?? 0))
    .filter((n) => Number.isFinite(n));
  // Numbered off the HIGHEST present, never the count: deleting day-break:1 from
  // [1, 2] would otherwise mint day-break:2 again and collide on save.
  return `day-break:${Math.max(0, ...used) + 1}`;
}

/**
 * What the "Weights: N/100" readout shows. Dividers carry no weight and are not steps,
 * so counting them would make a correct quest unpublishable.
 */
export function enabledWeightTotal(rows: readonly { stepCode: string; enabled: boolean; weight: string }[]): number {
  return rows
    .filter((row) => row.enabled && !isDayBreakStepCode(row.stepCode))
    .reduce((memo, row) => memo + (Number(row.weight) || 0), 0);
}

/**
 * The step type for a row's code — the catalog entry, or the `custom` base for a
 * `custom:N`. Returns undefined for a code no longer in the catalog, which the scorer
 * treats as "skip this step" rather than failing the whole quest.
 */
export function resolveStepType(stepCode: string): QuestStepType | undefined {
  // A divider resolves to nothing on purpose: it is never scored, never rendered as a
  // step, and never production-ready-checked. The scorer reads the code itself.
  if (isDayBreakStepCode(stepCode)) return undefined;
  if (isCustomStepCode(stepCode)) return QUEST_STEP_CATALOG.custom;
  return QUEST_STEP_CATALOG[stepCode];
}
export type StepOverrides = Partial<Record<(typeof OVERRIDABLE_FIELDS)[number], string | number>>;

/**
 * The step whose MEASUREMENT a row is scored by.
 *
 * A custom step may borrow another step's measurement via `measures`; the borrowed entry
 * supplies progress, the `requires` gate, the completion mode and the window — never the
 * copy. An unknown or self-referential `measures` is ignored rather than throwing: a bad
 * admin value must degrade to a click step, not take the quest down.
 *
 * One function, because two readers must agree on it exactly — the scorer, and the rule
 * that decides whether a step may recur. If they disagreed, a custom step could pass the
 * recurrence check under one measurement and be scored under another.
 */
export function measuringStepFor(
  stepCode: string,
  overrides?: { measures?: unknown } | null
): QuestStepType | undefined {
  const base = resolveStepType(stepCode);
  if (!base) return undefined;
  const borrowedCode = overrides?.measures;
  const borrowed =
    typeof borrowedCode === 'string' && borrowedCode && borrowedCode !== stepCode
      ? QUEST_STEP_CATALOG[borrowedCode]
      : undefined;
  return borrowed ?? base;
}

/** What a caller may change about how a step is worded — a bounty's own bar. */
export type StepLabelContext = { target?: number };

/** The `QuestSubject` fields a count step can declare as its `metric`. */
export type CountMetric = {
  [K in keyof QuestSubject]-?: NonNullable<QuestSubject[K]> extends number ? K : never;
}[keyof QuestSubject];

/**
 * "2 of 5" — the progress fragment every count label ends with.
 *
 * With no `ctx` it renders exactly the string these labels always rendered, so quests are
 * unaffected; with `ctx.target` it renders against that bar instead.
 */
function tally(count: number | null | undefined, bar: number, ctx?: StepLabelContext): string {
  const of = ctx?.target ?? bar;
  return `${Math.min(count ?? 0, of)} of ${of}`;
}

/**
 * A step's line without its running count — "Rate another artist's piece", not "… — 2 of 5".
 * For copy that outlives the moment: a notification, an email, a pitch to a visitor. The
 * count is stale as soon as she acts, and beside "— 50 points" it reads as two numbers.
 */
export function withoutTally(label: string): string {
  return label.replace(/\s+—\s+\d+ of \d+.*$/u, '');
}

function ratio(value: number | null | undefined, bar: number): number {
  if (!bar) return 0;
  return Math.max(0, Math.min(1, (value ?? 0) / bar));
}

/** Coverage of her own pieces — 0 pieces means 0 coverage, not vacuous completion. */
function coverage(value: number | null | undefined, imageCount: number | null | undefined): number {
  const total = imageCount ?? 0;
  if (total <= 0) return 0;
  return ratio(value, total);
}

const needsAPiece = (subject: QuestSubject) => (subject.imageCount ?? 0) > 0;

/**
 * "takes 3 min" / "takes 30 sec" — one formatter, because eight surfaces render this and
 * they all used to hard-code ` min` after the number.
 *
 * Sub-minute durations are the point (Nick, 2026-09-26): a step worth half a minute should
 * say so, and "takes 0.5 min" is not something a person writes. So `minutes` carries
 * fractions and this turns them into seconds at the boundary.
 *
 * Seconds are rounded to the nearest 5 so an admin typing 0.4 gets "25 sec" rather than
 * "24 sec" — a duration is a promise about effort, not a measurement.
 */
export function formatStepDuration(minutes: number | null | undefined): string | null {
  if (minutes == null || !Number.isFinite(minutes) || minutes <= 0) return null;
  if (minutes >= 1) {
    // A fractional value above a minute rounds to whole minutes: "1.5" is "2 min", not
    // "1 min 30 sec" — the label is a glance, not a stopwatch.
    return `${Math.round(minutes)} min`;
  }
  const seconds = Math.max(5, Math.round((minutes * 60) / 5) * 5);
  return `${seconds} sec`;
}

/**
 * A SPARK STEP — the shape the high-performing broadcasts already proved.
 *
 * Every one of these is the same move: Arty has already done the work, and the step
 * is an invitation to go and look at it. That is why the emails work, and why the
 * label is "I wrote you a product description" rather than "write a product
 * description" — the artist is being handed something, not assigned something.
 *
 * The link carries the spark id and the prompt VERBATIM, so an artist arriving from
 * a quest gets the identical Arty conversation as one arriving from the email; the
 * measurement is that spark's own completions. Adding the next one is this call and
 * nothing else — no subject field, no subquery, no resolver change.
 */
function sparkStep(input: {
  code: string;
  sparkId: number;
  adminLabel: string;
  label: string;
  evidence: string;
  why: string;
  message: string;
  cta?: string;
  minutes?: number;
  requiresAPiece?: boolean;
  productionReady?: boolean;
}): QuestStepType {
  const productionReady = input.productionReady !== false;
  return {
    code: input.code,
    completion: 'click',
    // A spark run is a thing she has DONE, but it completes on click and its measurement
    // is that spark's own completions — there is nothing here a pre-join row can satisfy
    // that a post-join one could not, so windowing it would only hide real history.
    scope: 'lifetime',
    adminLabel: input.adminLabel,
    adminMeasurement: productionReady
      ? `spark ${input.sparkId} completions (completions.completionTypeId = ${input.sparkId}), bar of 1`
      : `LOCAL ONLY — spark ${input.sparkId} unconfirmed in production. Do not publish.`,
    label: () => input.label,
    evidence: input.evidence,
    why: input.why,
    minutes: input.minutes ?? 1,
    ...(input.requiresAPiece === false ? {} : { requires: needsAPiece }),
    href: buildSparklinkUrl(
      { sparkId: String(input.sparkId), message: input.message, destination: 'community' },
      { origin: ARTHELPER_ORIGIN }
    ),
    // The href stays absolute so the link is still correct if anything ever falls back
    // to opening it directly; `arty` means it is normally never navigated to at all.
    opens: 'arty',
    ...(input.cta ? { cta: input.cta } : {}),
    progress: (s) => ratio(s.sparkCompletionCounts?.[String(input.sparkId)], 1),
    ...(productionReady ? {} : { productionReady: false }),
  };
}

export const QUEST_STEP_CATALOG: Record<string, QuestStepType> = {
  pieces: {
    code: 'pieces',
    completion: 'measured',
    scope: 'lifetime',
    adminLabel: 'Build a body of work (12 pieces)',
    adminMeasurement: 'images owned by the artist, against the 12-piece seller median',
    label: (s) =>
      `Add more artwork — ${Math.min(s.imageCount ?? 0, BODY_OF_WORK_BAR)} of ${BODY_OF_WORK_BAR}, enough to show a body of work instead of a sample`,
    evidence: `Artists who sell their work hold ${BODY_OF_WORK_BAR} pieces.`,
    why: `A buyer deciding whether to take you seriously counts what is there. ${BODY_OF_WORK_BAR} is also enough to post for a month without repeating yourself, which is why the Co-pilot lands on the same number from the other direction.`,
    href: '/my-artwork',
    opens: 'artwork-modal',
    progress: (s) => ratio(s.imageCount, BODY_OF_WORK_BAR),
  },
  reviewed: {
    code: 'reviewed',
    completion: 'click',
    scope: 'lifetime',
    adminLabel: 'Get an Art Review',
    adminMeasurement: 'pieces that have been through Art Review, over pieces owned',
    label: () => 'You have a new art review waiting',
    evidence: 'A review tells you which pieces are ready to sell before a buyer decides for you.',
    why: 'You know your own work too well to see it cold. An Art Review reads a piece back to you the way someone meeting it for the first time would, so the strongest ones go first instead of whatever you happened to upload last.',
    minutes: 1,
    requires: needsAPiece,
    href: ART_REVIEW_URL,
    opens: 'new-tab',
    cta: 'Read your review',
    progress: (s) => coverage(s.reviewedCount, s.imageCount),
  },
  priced: {
    code: 'priced',
    completion: 'click',
    scope: 'lifetime',
    adminLabel: 'Get a pricing analysis',
    adminMeasurement: 'pieces that have been through Price My Art, over pieces owned',
    label: () => 'I priced your pieces — I think you are low',
    evidence: 'Pricing used to be behind a paywall. It is free now, and unpriced work cannot sell.',
    why: 'Pricing your own work is the thing artists say they find hardest, and guessing low is the expensive mistake. A pricing analysis gives you a number to argue with instead of a blank field — and work with no price on it reads to a buyer as work that is not for sale.',
    minutes: 1,
    requires: needsAPiece,
    href: ART_PRICING_URL,
    opens: 'new-tab',
    cta: 'See the pricing',
    progress: (s) => coverage(s.pricedCount, s.imageCount),
  },
  idealCollector: {
    code: 'idealCollector',
    completion: 'click',
    scope: 'lifetime',
    adminLabel: 'Know your ideal collector (spark 380)',
    adminMeasurement: 'Ideal Collector spark completions (completionTypeId 380), bar of 1',
    label: () => 'I found your ideal collector — it is not who you would guess',
    evidence: 'Arty reads your whole body of work and tells you who it is for.',
    why: 'Everything downstream of this gets easier once you can picture the person: which pieces to lead with, what to write under them, and which channel is worth your time. Guessing at "everyone" is why most art marketing goes nowhere.',
    minutes: 1,
    requires: needsAPiece,
    href: IDEAL_COLLECTOR_URL,
    opens: 'arty',
    progress: (s) => ratio(s.idealCollectorCount, 1),
  },
  mockups: {
    code: 'mockups',
    completion: 'click',
    scope: 'lifetime',
    adminLabel: 'Make a room mockup',
    adminMeasurement: 'pieces carrying a saved mockup, over pieces owned',
    label: () => 'Show a piece on a wall — nobody can judge size from a crop',
    evidence: '35% of artists who sell have made a mockup of their work.',
    why: 'A photograph of a canvas tells a buyer nothing about how big it is or what it would look like above their sofa. One mockup answers both questions in a single image, without you photographing anything twice.',
    minutes: 1,
    requires: needsAPiece,
    href: MOCKUPS_URL,
    opens: 'new-tab',
    cta: 'Make a mockup',
    progress: (s) => coverage(s.mockupCount, s.imageCount),
  },
  profileFields: {
    code: 'profileFields',
    completion: 'measured',
    scope: 'lifetime',
    adminLabel: 'Finish the profile',
    adminMeasurement: 'the shipped weighted profile-completeness score (profileCompleteness())',
    label: () => 'See your profile the way a buyer sees it',
    evidence: 'Your profile is the page a buyer lands on. It is worth finishing, but it is not what sells work.',
    why: 'When someone finds your work, this is where they go to decide whether you are a real artist. It is worth finishing — and it is the smallest part of this score on purpose, because a perfect profile with two pieces behind it still sells nothing.',
    minutes: 5,
    href: '/profile',
    // Our own page — the explainer opens in place and the member walks there from it,
    // rather than being thrown into a second tab pointing back at this same app.
    opens: 'embed',
    progress: (s) => {
      if (s.profileEverUpdated) return 1;
      // Not yet edited: the weighted score paints partial credit, clamped below complete,
      // so only a real edit (or her own checkbox) finishes the step.
      return Math.min(0.95, profileCompleteness(s.profile ?? null).score / 100);
    },
  },
  // The 100% bar (Nick, 2026-11-03). profileFields finishes on any edit; this one only
  // when the same weighted score the profile meter shows reaches its maximum.
  profileComplete: {
    code: 'profileComplete',
    completion: 'measured',
    scope: 'lifetime',
    adminLabel: 'Profile at 100%',
    adminMeasurement: 'the shipped weighted profile-completeness score (profileCompleteness()) reaching 100',
    label: () => 'Get your profile to 100%',
    evidence: 'Your profile is the page a buyer lands on to decide whether you are a real artist.',
    why: 'Every empty field is a question a buyer has to answer on their own. Fill in the photo, bio, statement and the rest, and the meter on your profile reaches 100%.',
    minutes: 10,
    href: '/profile',
    opens: 'embed',
    cta: 'Finish your profile',
    progress: (s) => profileCompleteness(s.profile ?? null).score / PROFILE_COMPLETENESS_MAX,
  },
  connectChannel: {
    code: 'connectChannel',
    completion: 'measured',
    scope: 'lifetime',
    adminLabel: 'Connect a social media channel',
    adminMeasurement: 'connected user_integrations, bar of 1',
    label: () => 'Connect a social media channel — work nobody sees does not sell',
    evidence: '22% of artists who sell have connected at least one channel.',
    why: 'Your gallery is one page that nobody arrives at by accident. Connecting a single account is the difference between work that exists and work that gets in front of the people who might buy it.',
    minutes: 2,
    href: '/integrations',
    opens: 'embed',
    progress: (s) => ratio(s.connectedChannelCount, 1),
  },
  postingCadence: {
    code: 'postingCadence',
    completion: 'measured',
    scope: 'lifetime',
    adminLabel: 'Post 3 days a week (automated)',
    adminMeasurement: 'distinct posting days per week through connected channels',
    label: () => `Post ${POSTING_CADENCE_BAR} days a week — automated exposure, without doing it by hand`,
    evidence:
      'Almost nobody keeps this up by hand. It is a goal rather than a measured norm, and it is what automation is for.',
    why: 'This is the one bar here we set rather than measured, and it is the one you should not try to hit by hand. It is a time problem, not a discipline problem — which is exactly the job the Co-pilot exists to take off you.',
    aspirational: true,
    href: '/calendar',
    opens: 'embed',
    progress: (s) => ratio(s.postingDaysPerWeek, POSTING_CADENCE_BAR),
  },
  postForCritique: {
    code: 'postForCritique',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Post a piece for critique',
    adminMeasurement: 'community posts authored, excluding the welcome-wizard intro post, bar of 1, since joining',
    label: () => 'Ask for an art critique',
    evidence: 'Feedback starts with a post. There is no queue you can join by waiting.',
    why: 'This is the step everything else here is for. Artists who post a piece and say what they want looked at get real answers; your work sitting quietly in a gallery gets scrolled past, however good it is.',
    minutes: 2,
    requires: needsAPiece,
    href: '/community',
    opens: 'critique-post',
    progress: (s) => ratio(s.postForCritiqueCount, 1),
  },
  askedForCritique: {
    code: 'askedForCritique',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Ask for your first critique',
    adminMeasurement: 'CRITIQUE community posts authored, bar of 1, since joining',
    label: () => 'Get your first critique',
    evidence: 'Feedback starts with a request. There is no queue you can join by waiting.',
    why: 'Say what you want looked at and post the piece. A request that names its question gets real answers; a quiet gallery gets scrolled past.',
    minutes: 2,
    requires: needsAPiece,
    href: '/community',
    opens: 'critique-post',
    progress: (s) => ratio(s.critiquePostCount, 1),
  },
  /**
   * A community post she chose to write. The welcome intro and the one-click critique are
   * the wizard's, so they do not count. Needs no artwork first and opens the ordinary
   * composer, so a brand-new member can do it and a daily "post something" bounty can ask
   * for it every day. `postForCritique` is the critique ask and reads a wider count.
   */
  communityPost: {
    code: 'communityPost',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Make a community post',
    adminMeasurement:
      'community posts she wrote herself, excluding onboarding intro and one-click critique posts, bar of 1, since joining',
    label: (s, ctx) => `Make a community post — ${tally(s.communityPostCount, 1, ctx)}`,
    metric: 'communityPostCount',
    bar: 1,
    evidence: 'A community is the posts in it. The feed only has what members put there.',
    why: 'Share what you are working on, ask a question, or show something you finished. A post is how other artists find you, and the ones who post are the ones who get answered.',
    minutes: 2,
    href: '/community?compose=1',
    opens: 'compose-post',
    progress: (s) => ratio(s.communityPostCount, 1),
  },
  // Taylor, 2026-11-03: "leaving a comment on a target post". The posts are listed on the
  // bounty (config.postIds); `{targetPostPath}` opens the next one she has not answered
  // and did not write.
  commentOnPosts: {
    code: 'commentOnPosts',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Comment on posts you pick',
    adminMeasurement:
      'different listed posts she commented on since joining and since the bounty went live, bar of 1 (set Count to reach for more); bounties only',
    label: (s, ctx) => `Join the conversation — ${tally(s.targetPostCommentCount, 1, ctx)}`,
    metric: 'targetPostCommentCount',
    bar: 1,
    evidence: 'A thread with real answers in it is the one people find, read and come back to.',
    why: 'Someone asked a question the whole community has an opinion on. Add yours — what you have learned is exactly what the next artist is looking for.',
    minutes: 3,
    href: '/community/{targetPostPath}',
    opens: 'embed',
    cta: 'Open the post',
    progress: (s) => ratio(s.targetPostCommentCount, 1),
  },
  // Nick, 2026-09-13: this one opens the Sparks app ON the piece, framed, nav bar and all —
  // "it shows the navigation bar so that the user knows and learns this is done in the
  // Sparks app". `{imageId}` is filled from the subject at score time; with no piece yet it
  // degrades to the Sparks front door rather than a URL to nothing.
  productDescription: {
    ...sparkStep({
      code: 'productDescription',
      sparkId: PRODUCT_DESCRIPTION_SPARK_ID,
      adminLabel: `Product description (spark ${PRODUCT_DESCRIPTION_SPARK_ID})`,
      label: 'I wrote you a product description — come and read it',
      evidence: 'A piece with nothing written under it gives a buyer nothing to hold on to.',
      why: 'The description is the part most artists leave blank, because writing about your own work from the inside is genuinely hard. Arty writes the first one for you from the piece itself, so you are editing a draft instead of facing an empty box — and you can keep it, change it, or throw it away.',
      message: 'Write a product description for my newest piece that I could put straight on the listing.',
      productionReady: false,
    }),
    href: `https://sparks.arthelper.com/run/image/{imageId}/${PRODUCT_DESCRIPTION_SPARK_ID}`,
    opens: 'embed',
  },
  // Nick, 2026-09-13: this one opens the Sparks app ON the piece, framed, nav bar and all —
  // "it shows the navigation bar so that the user knows and learns this is done in the
  // Sparks app". `{imageId}` is filled from the subject at score time; with no piece yet it
  // degrades to the Sparks front door rather than a URL to nothing.

  artAnalysis: sparkStep({
    code: 'artAnalysis',
    sparkId: ART_ANALYSIS_SPARK_ID,
    adminLabel: `Arty reviewed your piece (spark ${ART_ANALYSIS_SPARK_ID}, "Art Analysis")`,
    label: 'Arty reviewed your piece — read what she said',
    evidence: 'The honest read of a piece is the one its own artist cannot give it.',
    why: 'You are too close to your own work to meet it cold. This is the same review the first-upload email sends, asked in the same words — an honest critique of the piece itself, from someone seeing it for the first time.',
    // VERBATIM from `send-first-upload-spark-email`, so a quest arrival gets the
    // identical Arty conversation as the email that already earns its opens.
    message: SPARKLINK_FIRST_UPLOAD_MESSAGE,
    productionReady: false,
  }),
  contentBatch: sparkStep({
    code: 'contentBatch',
    sparkId: CONTENT_BATCH_SPARK_ID,
    adminLabel: `Content batch (spark ${CONTENT_BATCH_SPARK_ID}) — day 5 of the onboarding series`,
    label: 'Your content is written — captions, posts and listings',
    evidence: 'A week of posts, built from the pieces you already uploaded.',
    why: 'Writing about your own work every day is the part that quietly stops most artists posting at all. This is a batch of it already drafted from your actual pieces, so the job becomes choosing and editing rather than staring at an empty box.',
    message: 'I need help with content for the next 7 days',
    cta: 'Read it',
  }),
  magazineFeature: sparkStep({
    code: 'magazineFeature',
    sparkId: MAGAZINE_FEATURE_SPARK_ID,
    adminLabel: `Magazine feature (spark ${MAGAZINE_FEATURE_SPARK_ID}) — day 7 of the onboarding series`,
    label: 'I wrote a magazine feature about your work',
    evidence: 'The kind of piece a magazine would run about you, written from your work.',
    why: 'Nobody writes about you until somebody writes about you. This is a real feature about your practice you can lift lines from for your statement, your about page, or the next time somebody asks what you make.',
    message:
      'Write a magazine feature about my art - a short article that explores what makes my work distinctive, the way a magazine would profile me.',
    cta: 'Read the feature',
  }),
  openContest: {
    code: 'openContest',
    completion: 'click',
    scope: 'lifetime',
    adminLabel: 'Open contest entry — day 1 of the onboarding series',
    adminMeasurement: 'click-through only; entries are judged outside the app',
    label: () => "There's an open contest running — put your work in",
    evidence: 'An open category, judged on the work rather than on who already follows you.',
    why: 'Entering costs you nothing and puts your pieces in front of people who are there to look properly. It is also the lowest-stakes way to find out how your work reads to strangers, which is the thing most artists never get told.',
    minutes: 2,
    requires: needsAPiece,
    href: OPEN_CONTEST_URL,
    opens: 'embed',
    progress: () => 0,
  },
  /**
   * THE SAME ASK AS `openContest`, MEASURED. Same words, same embed — the Series' day-1
   * line — but it completes on a real entry rather than on the click through, so a bounty
   * built on it pays for entering, not for looking. `openContest` stays as it is for the
   * quests already using it. `href` is the static fallback: a member's bounty swaps in the
   * open Open Category round (`liveStepHref` in the api's bounty service), so a contest
   * rolling over needs no edit here.
   */
  contestEntry: {
    code: 'contestEntry',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Enter a contest (measured)',
    adminMeasurement:
      'top-level approved comments on an active contest post, not her own, made while it was open, bar of 1, since joining',
    label: () => "There's an open contest running — put your work in",
    evidence: 'An open category, judged on the work rather than on who already follows you.',
    why: 'Entering costs you nothing and puts your pieces in front of people who are there to look properly. It is also the lowest-stakes way to find out how your work reads to strangers, which is the thing most artists never get told.',
    minutes: 2,
    requires: needsAPiece,
    href: OPEN_CONTEST_URL,
    opens: 'embed',
    progress: (s) => ratio(s.contestEntryCount, 1),
  },
  /**
   * FINISHING THE WIZARD. State she has reached, so lifetime: a bounty may use it only
   * inside the onboarding series, where every member it can pay is new.
   */
  completeOnboarding: {
    code: 'completeOnboarding',
    completion: 'measured',
    scope: 'lifetime',
    adminLabel: 'Finish onboarding',
    adminMeasurement: 'users.welcomeCompletedAt is set',
    label: () => 'Finish setting up your account',
    evidence: 'Your profile is how other artists find you, and how Arty knows what to make for you.',
    why: 'Everything else here is built from what you tell us in those few minutes — your work, your medium, where you are. Finishing it is what turns a blank account into one that recommends the right artists and writes things that sound like you.',
    minutes: 3,
    href: ONBOARDING_URL,
    opens: 'new-tab',
    progress: (s) => (s.onboardingCompleted ? 1 : 0),
  },
  /**
   * CREATE OR JOIN A GUILD. Creating is the valuable half — she may be the first of her
   * artist friends here, and a Guild is how she brings them — but either counts. State, so
   * lifetime: leaving and rejoining makes a fresh membership row, which is exactly why this
   * must never take a cadence.
   */
  guildMember: {
    code: 'guildMember',
    completion: 'measured',
    scope: 'lifetime',
    adminLabel: 'Create or join a Guild',
    adminMeasurement: 'active guild_members rows (created, approved or invite-accepted), bar of 1',
    label: () => 'Create or join a Guild',
    evidence: 'A small, private group of artists — share work in progress and celebrate each other.',
    why: "A Guild is a small, private group of artists — share work in progress, celebrate each other, keep up with each other's creative lives. Join one, or create one and invite your friends!",
    minutes: 2,
    href: GUILDS_URL,
    // Our own app, so it opens in place rather than in a tab she has to find her way back from.
    opens: 'embed',
    progress: (s) => ratio(s.guildMembershipCount, 1),
  },
  artDebate: {
    code: 'artDebate',
    completion: 'click',
    scope: 'lifetime',
    adminLabel: 'Art debate — day 3 of the onboarding series',
    adminMeasurement: 'click-through only; reading the thread is the point',
    label: () => 'Share your take in this 🔥 Community discussion (or just upvote)',
    evidence:
      '“Does showing your process take something away from the finished work?” — two defensible sides, no wrong answer.',
    why: 'You do not have to post your own work to take part, and you do not have to go looking either — this is a busy one we picked for you. Read where it has got to, say which side you land on, and a sentence is plenty.',
    minutes: 3,
    href: ART_DEBATE_URL,
    opens: 'embed',
    cta: 'Join the debate',
    progress: () => 0,
  },
  votedInPoll: {
    code: 'votedInPoll',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Vote in a community poll (1)',
    adminMeasurement: 'community_poll_votes rows for the member, bar of 1, since joining',
    label: () => 'Vote in an open poll — no writing at all',
    evidence: 'A poll is an opinion you can give without composing a sentence.',
    why: 'Plenty of artists here would rather not post, and this community should still hear from you. A vote is one tap, it is anonymous in the tally, and it is the smallest possible way to stop being a spectator.',
    minutes: 1,
    // Repointed off the /community embed onto the poll picker: same relationship
    // answerRecentCritique (bar 1) has to critiquesGiven (bar 5) — one panel, two bars.
    href: '/community/polls',
    opens: 'poll-answer',
    progress: (s) => ratio(s.pollVoteCount, 1),
  },
  artBook: {
    code: 'artBook',
    completion: 'click',
    scope: 'lifetime',
    adminLabel: 'Art book — the 2026-08-24 broadcast (best click rate in 30d)',
    adminMeasurement: 'click-through only; the book is built by a separate app',
    label: () => 'I made you a book out of your art',
    evidence: 'Your pieces, laid out as a book, without you formatting anything.',
    why: 'Seeing your own work bound together as a body rather than a grid is the moment most artists realise they have more than they thought. It is also the easiest thing here to show somebody who asks what you make.',
    minutes: 1,
    requires: needsAPiece,
    href: ART_BOOK_URL,
    opens: 'new-tab',
    cta: 'See your book',
    progress: () => 0,
  },
  /**
   * THE ONBOARDING SERIES' ARTIST CARDS (#4215) — one match axis each, shown as the digest's
   * cards with the item's own title. Picked when delivered, so the step itself measures
   * nothing; a series item pays on a real Connect or Follow from its card. As a quest step
   * each still reads as the artist-matches panel scoped to nothing narrower than it is.
   */
  coffeeMatches: {
    code: 'coffeeMatches',
    completion: 'click',
    scope: 'lifetime',
    adminLabel: 'Artists close enough for coffee — onboarding series, day 0',
    adminMeasurement: 'click-through only; cards are picked on delivery from the coffee axis',
    label: () => 'Close enough for coffee',
    evidence: 'Artists working within travelling distance of your studio.',
    why: 'The artists who end up mattering most are often the ones you can actually sit across a table from. These work near you, so a hello here can turn into a studio visit, a shared stall or a show you go to together.',
    minutes: 2,
    href: ARTIST_MATCHES_URL,
    opens: 'artist-matches',
    progress: () => 0,
  },
  visualMatches: {
    code: 'visualMatches',
    completion: 'click',
    scope: 'lifetime',
    adminLabel: 'Artists whose work rhymes with yours — onboarding series, day 5',
    adminMeasurement: 'click-through only; cards are picked on delivery from the visual axis',
    label: () => 'Artists whose work rhymes with yours',
    evidence: 'Similar palette, surface and subject to the work you have uploaded.',
    why: 'Seeing your own concerns worked out in somebody else’s hands is the quickest way to see your work fresh. These were picked from the pieces you uploaded, so you share a visual language before either of you says a word.',
    minutes: 2,
    requires: needsAPiece,
    href: ARTIST_MATCHES_URL,
    opens: 'artist-matches',
    progress: () => 0,
  },
  identityMatches: {
    code: 'identityMatches',
    completion: 'click',
    scope: 'lifetime',
    adminLabel: 'Artists on a similar path — onboarding series, day 10',
    adminMeasurement: 'click-through only; cards are picked on delivery from the identity axis',
    label: () => 'Artists on a similar path',
    evidence: 'Similar practice, medium and stage of career.',
    why: 'Someone a step or two along the same road can tell you what is coming better than any guide can. These artists work the way you do and are at a similar point, so what worked for them is worth asking about.',
    minutes: 2,
    href: ARTIST_MATCHES_URL,
    opens: 'artist-matches',
    progress: () => 0,
  },
  /**
   * THE ONBOARDING SERIES' LIVE THREADS (#4215). The link is picked when the item is
   * delivered — the newest platform announcement, or the thread that took off this week —
   * so the href here is only the fallback. A series item writes `{title}` into its label to
   * name the thread; the defaults name none, so a quest that borrows one still reads.
   */
  platformHighlight: {
    code: 'platformHighlight',
    completion: 'click',
    scope: 'lifetime',
    adminLabel: 'Platform highlight — onboarding series, day 8 (the newest announcement)',
    adminMeasurement: 'click-through only; the thread is the newest site-wide announcement when delivered',
    label: () => 'Platform highlight: the thread we are pointing everyone to',
    evidence: 'A conversation the team picked out for the whole community.',
    why: 'You do not need to post your own work to be part of this place. This is the thread we have pinned for everyone this week, so it is where you will find the most people reading and replying right now.',
    minutes: 3,
    href: '/community',
    opens: 'embed',
    cta: 'Join the thread',
    progress: () => 0,
  },
  hotThread: {
    code: 'hotThread',
    completion: 'click',
    scope: 'lifetime',
    adminLabel: 'Hot thread — onboarding series, day 13 (only when one takes off)',
    adminMeasurement: 'click-through only; the most trending thread of the week, and only past its reply bar',
    label: () => 'There is a thread everyone is talking about',
    evidence: 'One that genuinely took off this week.',
    why: 'Threads like this are rare, and they are where you get to hear how a lot of working artists really think. You can read where it has got to before you say anything, and a sentence of your own is plenty.',
    minutes: 3,
    href: '/community',
    opens: 'embed',
    cta: 'Join the thread',
    progress: () => 0,
  },
  welcomeRewards: {
    code: 'welcomeRewards',
    completion: 'click',
    scope: 'lifetime',
    adminLabel: 'Welcome gift — onboarding series, day 0 (only when it unlocks a reward)',
    adminMeasurement:
      'click-through only; the reward it unlocks and the unlocked count are read for each member when delivered',
    label: () => 'Welcome aboard — {points} points',
    evidence: 'Unlocks {reward}.',
    why: 'Points are yours to spend on rewards. This gift covers one of them today, so you can see what a reward looks like before you earn any more.',
    minutes: 1,
    href: '/points?src=welcome_gift',
    opens: 'new-tab',
    cta: 'View rewards ({unlocked} unlocked)',
    progress: () => 0,
  },
  profileMirror: {
    code: 'profileMirror',
    completion: 'click',
    scope: 'lifetime',
    adminLabel: "Profile through a collector's eyes — the 2026-08-29 broadcast",
    adminMeasurement: 'click-through only; the read happens in the profile app',
    label: () => 'I looked at your profile the way a collector does',
    evidence: 'The gaps a buyer notices are rarely the ones you would have guessed.',
    why: 'You cannot read your own profile cold — you already know everything it leaves out. This is the same read a collector makes in about eight seconds, written down, so you can fix the two things that actually cost you.',
    minutes: 3,
    href: PROFILE_MIRROR_URL,
    opens: 'new-tab',
    progress: () => 0,
  },
  soloShow: sparkStep({
    code: 'soloShow',
    sparkId: SOLO_SHOW_SPARK_ID,
    adminLabel: `First solo show (spark ${SOLO_SHOW_SPARK_ID}) — the 2026-08-30 broadcast`,
    label: 'I curated your first solo show',
    evidence: 'A hang, in an order, with a reason for each piece next to the last.',
    why: 'Curating is the skill nobody teaches artists, and it is what turns a folder of work into a show. Seeing your own pieces sequenced — and told why — is the fastest way to learn what you have been making without noticing.',
    message: 'Curate my first solo show - which pieces, in what order, and why each one sits where it does.',
  }),
  ruleBreaker: sparkStep({
    code: 'ruleBreaker',
    sparkId: RULE_BREAKER_SPARK_ID,
    adminLabel: `Rule breaker (spark ${RULE_BREAKER_SPARK_ID}) — the 2026-08-14 broadcast`,
    label: 'You break the same rule in every piece',
    evidence: 'A pattern runs through your work that you are too close to name.',
    why: 'Every artist has one thing they do against the grain, and it is usually the thing their work is actually about. Having it named — with the pieces where you do it — gives you language for your own style that you can use everywhere else.',
    message:
      'Go through my work and find the rule I break in almost every piece. Name it, show me where I break it, and tell me why it works for me.',
  }),
  galleryMatches: sparkStep({
    code: 'galleryMatches',
    sparkId: GALLERY_MATCHES_SPARK_ID,
    adminLabel: `Gallery matches (spark ${GALLERY_MATCHES_SPARK_ID}) — the 2026-08-07 broadcast`,
    label: 'I found galleries near you that would love your art',
    evidence: 'Who to contact, why your work fits, and a message already drafted.',
    why: 'Approaching a gallery stalls on two things: not knowing which ones are a fit, and not knowing what to say. Both are done for you here — the shortlist is based on your actual work, and the note is written, so the only step left is sending it.',
    message: 'Find galleries near me that would love my art, and write me a short message I can send each one.',
  }),
  madeReel: {
    code: 'madeReel',
    completion: 'click',
    scope: 'lifetime',
    adminLabel: 'Reel — the 2026-10-15 broadcast',
    adminMeasurement: 'click-through only; the Reel is built by the autoposter',
    label: () => 'I made you a Reel. Wow, it’s good.',
    evidence: 'I picked the piece, built the Reel and wrote the words. All that is left is posting it.',
    why: 'Video is what the feeds push hardest, and it is the format artists put off longest because making one feels like a second job. This one is already made from your own work, so the only decision left is whether to post it.',
    minutes: 1,
    requires: needsAPiece,
    href: REEL_URL,
    opens: 'new-tab',
    cta: 'See your Reel',
    progress: () => 0,
  },
  madeCarousel: {
    code: 'madeCarousel',
    completion: 'click',
    scope: 'lifetime',
    adminLabel: 'Carousel — the 2026-10-26 broadcast',
    adminMeasurement: 'click-through only; the carousel is built by the autoposter',
    label: () => 'I made you a carousel. Swipe through it.',
    evidence: 'I picked the pieces, built the carousel and wrote the caption. All that is left is posting it.',
    why: 'A carousel shows a body of work rather than a single piece, and people who swipe stay on your post longer than people who scroll past one image. It is built from your own pieces, caption included.',
    minutes: 1,
    requires: needsAPiece,
    href: CAROUSEL_URL,
    opens: 'new-tab',
    cta: 'See your carousel',
    progress: () => 0,
  },
  collectorVisit: sparkStep({
    code: 'collectorVisit',
    sparkId: COLLECTOR_VISIT_SPARK_ID,
    adminLabel: `Collector visit (spark ${COLLECTOR_VISIT_SPARK_ID}) — the 2026-09-14 broadcast`,
    // The broadcast said a collector picked it; this stays in her inbox, so it says what it is.
    label: 'I played a collector walking through your work — see which piece I picked',
    evidence: 'Which piece stopped them, what they noticed, and the wall it is going on.',
    why: 'Artists see their work from the easel; buyers see it from across a room. Hearing one walk through your pieces — what pulls them in and where they picture it hanging — tells you what your work is doing for the people who might buy it.',
    message:
      'Roleplay a real collector browsing my work - who you are, the piece that caught your eye, where it is going in your home, and what made you decide.',
    cta: 'Meet your collector',
  }),
  nextPiece: sparkStep({
    code: 'nextPiece',
    sparkId: NEXT_PIECE_SPARK_ID,
    adminLabel: `Next piece (spark ${NEXT_PIECE_SPARK_ID}) — the 2026-09-01 broadcast`,
    label: 'I found the piece you haven’t made yet',
    evidence: 'Not a direction — the actual next piece, and which of your work led me to it.',
    why: 'The hardest part of the next piece is often deciding what it is. This one is concrete enough to start this week — what is in it, what size, and what carries over from the work you have already made.',
    message:
      "Look at my whole collection and tell me the piece I haven't made yet - the actual next piece my work is pointing at, not a vague direction. Be concrete enough that I could start it this week: what's in it, what size, what stays from my existing work and what's deliberately different. Name the pieces of mine that led you there, and tell me the one thing that will be hard about it.",
    cta: 'See your next piece',
  }),
  almostBought: sparkStep({
    code: 'almostBought',
    sparkId: ALMOST_BOUGHT_SPARK_ID,
    adminLabel: `Almost bought (spark ${ALMOST_BOUGHT_SPARK_ID}) — the 2026-09-27 broadcast`,
    label: 'Imagine someone almost bought your piece — here is what stopped them',
    evidence: 'What drew them in, how close they got, and the one thing that stopped them.',
    why: 'Most lost sales are silent: nobody tells you why they walked away. Hearing a near-buyer explain it usually turns up something you can fix — a price, a photo, a missing detail.',
    message:
      'Somebody almost bought one of my pieces and did not - be them, and tell me what stopped you, what nearly got you over the line, and what would have changed your mind.',
    cta: 'See who almost bought it',
  }),
  documentary: sparkStep({
    code: 'documentary',
    sparkId: DOCUMENTARY_SPARK_ID,
    adminLabel: `Documentary (spark ${DOCUMENTARY_SPARK_ID}) — the 2026-09-09 broadcast`,
    label: 'I wrote a documentary about your work',
    evidence: 'The opening shot, three scenes on your own pieces, and the closing frame. It even has a title.',
    why: 'A film treatment has to decide what your work is about in one opening shot. Seeing that decision made about your own pieces gives you a story to tell about them — on your about page, in a post, or when somebody asks.',
    message:
      "Write the treatment for a short documentary about my work - the opening shot, what the film is actually about, three scenes on my real pieces, and a closing shot on the piece an audience wouldn't predict. Present tense, visual, and give the film a title.",
    cta: 'Read your treatment',
  }),
  wikipedia: sparkStep({
    code: 'wikipedia',
    sparkId: WIKIPEDIA_SPARK_ID,
    adminLabel: `Wikipedia page (spark ${WIKIPEDIA_SPARK_ID}) — the 2026-09-06 broadcast`,
    label: 'I wrote your Wikipedia page',
    evidence: 'Overview, style, themes, notable works — your actual pieces, cited by name.',
    why: 'An encyclopedia entry has to say plainly what your work is and which pieces matter most. Reading that about yourself is a quick way to find the words for your statement and to see which pieces carry your body of work.',
    message:
      "Write the Wikipedia entry my body of work would earn. Encyclopedic voice, third person, using my real name and my real piece titles. Sections: Overview, Style and technique, Themes, Notable works (three to five of mine, with why an entry would cite each), and Periods only if my work actually shows a shift. Invent nothing about my life - no birth year, no training, no galleries, no exhibitions, no awards. If a section needs biography you don't have, mark it to be added. Finish with the one thing my work would need more of to make the entry longer.",
    cta: 'Read your entry',
  }),
  artistMatches: {
    code: 'artistMatches',
    completion: 'click',
    scope: 'lifetime',
    adminLabel: 'Artist matches — the 2026-07-20 broadcast ("+ Connect" CTA)',
    adminMeasurement: 'click-through only; each connection is made on the artist',
    label: () => "I found 10 artists you'd really click with",
    evidence: 'Chosen from your work, not from who happens to be popular this week.',
    why: 'Connections here are mutual, so you start with none and make them one at a time. Ten artists picked for you removes the part nobody does — the going and looking — and leaves you a row of Connect buttons.',
    minutes: 3,
    href: ARTIST_MATCHES_URL,
    opens: 'artist-matches',
    progress: () => 0,
  },
  likeArtwork: {
    code: 'likeArtwork',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: "Like another artist's work (3)",
    adminMeasurement: "likes on other artists' images, bar of 3, since joining",
    label: (s, ctx) => `Like the work of a few artists — ${tally(s.artworkLikesGivenCount, 3, ctx)}`,
    metric: 'artworkLikesGivenCount',
    bar: 3,
    evidence: 'A like reaches the artist who made it, and costs you one tap.',
    why: 'This is the smallest thing on the list and the one most likely to come back to you: every like lands as a notification on the artist who made the piece, and artists look at who liked their work. The app picks three whose work is closest to yours, so there is nothing to search for.',
    minutes: 1,
    href: '/artists',
    opens: 'like-targets',
    progress: (s) => ratio(s.artworkLikesGivenCount, 3),
  },
  commentOnArtwork: {
    code: 'commentOnArtwork',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Comment on another artist’s piece (1)',
    adminMeasurement: 'approved image_comments left on another artist’s image, bar of 1, since joining',
    label: () => "Add a positive comment to someone else's work and brighten their day.",
    evidence: 'The first comment on a piece is the one its artist remembers.',
    why: 'Being the first person to say something about a stranger’s piece is the cheapest good thing you can do on this site, and it is how they find out you exist. The app names the artist and the piece, so there is nothing to search for.',
    minutes: 2,
    href: '/community',
    opens: 'comment-targets',
    progress: (s) => ratio(s.artworkCommentsGivenCount, 1),
  },
  commentOnOthers: {
    code: 'commentOnOthers',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Critique other artists (5 comments)',
    adminMeasurement: 'comments left on other artists, bar of 5, since joining',
    label: (s, ctx) => `Critique someone else's work — ${tally(s.commentsGivenCount, 5, ctx)}`,
    metric: 'commentsGivenCount',
    bar: 5,
    evidence:
      'Critique communities run on reciprocity. The artists who get the most feedback are the ones who give it.',
    why: "Leaving a real comment on someone else's piece is the fastest way to be seen by the people whose opinion you want — and reading other work closely is where most of the learning actually happens.",
    minutes: 2,
    // The critiques surface, not the community root: this step is asking for a critique,
    // and /community is a feed she then has to go hunting through for one to answer.
    href: '/community/critiques',
    opens: 'critique-answer',
    progress: (s) => ratio(s.commentsGivenCount, 5),
  },
  followArtists: {
    code: 'followArtists',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Follow artists (10)',
    adminMeasurement: 'artists followed, bar of 10, since joining',
    label: (s, ctx) => `Follow artists working near you — ${tally(s.followingCount, 10, ctx)}`,
    metric: 'followingCount',
    bar: 10,
    evidence: 'A feed of work like yours is where useful feedback and useful ideas both come from.',
    why: 'Who you follow decides what you see, and an empty feed gives you nothing to react to and nobody to be seen by. Ten is roughly where a feed stops being empty and starts being yours.',
    minutes: 2,
    href: '/artists',
    opens: 'artist-matches',
    progress: (s) => ratio(s.followingCount, 10),
  },
  described: {
    code: 'described',
    completion: 'measured',
    scope: 'lifetime',
    adminLabel: 'Title and describe each piece',
    adminMeasurement: 'pieces carrying both a title and a description, over pieces owned',
    label: () => 'Title and describe each piece — so you can find it again',
    evidence: 'A titled, described piece is one you can search for. An untitled one you have to scroll for.',
    why: 'Titles and descriptions are what make a portfolio searchable by you, not just readable by a visitor. They are also what every other surface — a listing, a post, a submission — pulls from later.',
    minutes: 5,
    requires: needsAPiece,
    href: '/art',
    opens: 'embed',
    progress: (s) => coverage(s.describedCount, s.imageCount),
  },
  specs: {
    code: 'specs',
    completion: 'measured',
    scope: 'lifetime',
    adminLabel: 'Record medium and size',
    adminMeasurement: 'pieces carrying artwork specs, over pieces owned',
    label: () => 'Record medium and size — the two things you always have to look up',
    evidence:
      'Medium and dimensions are the first questions asked of any piece, by a gallery, a buyer or a submission form.',
    why: 'These are the details that live in your head or on the back of the canvas, and the ones you have to go and measure again every time someone asks. Recording them once ends that.',
    minutes: 5,
    requires: needsAPiece,
    href: '/art',
    opens: 'embed',
    progress: (s) => coverage(s.specsCount, s.imageCount),
  },
  connectionsMade: {
    code: 'connectionsMade',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Make real connections (5)',
    adminMeasurement: 'connection requests she sent, bar of 5, since joining',
    label: (s, ctx) => `Make a few real connections — ${tally(s.connectionsMadeCount, 5, ctx)}`,
    metric: 'connectionsMadeCount',
    bar: 5,
    evidence: 'A connection is two-way, so it is the point where someone can actually message you back.',
    why: 'Following someone is a broadcast; connecting is a door. Five is enough that your feed stops being strangers and someone recognises your name the next time you post — which is when this place starts working for you.',
    minutes: 3,
    href: '/artists',
    opens: 'artist-matches',
    progress: (s) => ratio(s.connectionsMadeCount, 5),
  },
  critiquesGiven: {
    code: 'critiquesGiven',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Write critiques (5)',
    adminMeasurement: 'community_critiques authored on other artists, bar of 5, since joining',
    label: (s) =>
      `Provide a critique to an artist looking for feedback — ${Math.min(s.critiquesGivenCount ?? 0, 5)} of 5`,
    evidence: 'The Valued Critic badge starts at one critique somebody marks as helpful.',
    why: "Reading someone else's work closely is where most of the learning actually happens, and a critique queue only works if people give as well as ask. Write five and you are one of the reasons this place is worth posting to.",
    minutes: 5,
    href: '/community/critiques',
    opens: 'critique-answer',
    progress: (s) => ratio(s.critiquesGivenCount, 5),
  },
  /**
   * THE OTHER THREE WAYS SOMEBODY ASKS FOR HELP. A critique request is the long one; a
   * rating, a price and a poll are each a single tap to answer, which is exactly why they
   * sit unanswered — nobody goes looking for them, and the artist who asked is left
   * reading silence. Same panel shape as the critique step, no newcomer filter: the ask
   * is the invitation, whoever made it.
   */
  ratingsGiven: {
    code: 'ratingsGiven',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: "Rate other artists' work (5)",
    adminMeasurement: "community_art_ratings submitted on other artists' posts, bar of 5, since joining",
    label: (s, ctx) => `Rate another artist's piece — ${tally(s.artRatingsGivenCount, 5, ctx)}`,
    metric: 'artRatingsGivenCount',
    bar: 5,
    evidence: 'A rate-my-art post with no ratings tells the artist nothing at all.',
    why: "Somebody put a piece up and asked to be scored out of ten, which takes more nerve than it looks. You can answer in one tap, and the average only starts meaning something once enough people have. Five of these costs you a couple of minutes and settles five artists' questions.",
    minutes: 2,
    href: '/community/ratings',
    opens: 'rating-answer',
    progress: (s) => ratio(s.artRatingsGivenCount, 5),
  },
  pricesGiven: {
    code: 'pricesGiven',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: "Price other artists' work (5)",
    adminMeasurement: "community_pricing_submissions on other artists' posts, bar of 5, since joining",
    label: (s, ctx) => `Put a price on another artist's piece — ${tally(s.pricingGivenCount, 5, ctx)}`,
    metric: 'pricingGivenCount',
    bar: 5,
    evidence: 'Pricing is the question artists are least able to answer for themselves.',
    why: 'Deciding what your own work is worth is the hardest call an artist makes alone, and a single outside number is worth more than a week of second-guessing. Your estimate stays private until enough people have answered, so you are not anchoring anybody — you are giving them the one thing they cannot get from looking at their own piece.',
    minutes: 2,
    href: '/community/pricing',
    opens: 'pricing-answer',
    progress: (s) => ratio(s.pricingGivenCount, 5),
  },
  pollsVoted: {
    code: 'pollsVoted',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Vote in polls (5)',
    adminMeasurement: "community_poll_votes cast on other artists' posts, bar of 5, since joining",
    label: (s, ctx) => `Vote in another artist's poll — ${tally(s.pollVotesGivenCount, 5, ctx)}`,
    metric: 'pollVotesGivenCount',
    bar: 5,
    evidence: 'Most polls here are an artist choosing between two versions of the same piece.',
    why: 'Which crop, which colourway, which one to enter — these are the decisions artists stall on for days and settle in minutes once somebody else weighs in. Voting costs you one tap and hands the artist the thing they actually wanted, which is a second opinion rather than a compliment.',
    minutes: 1,
    href: '/community/polls',
    opens: 'poll-answer',
    progress: (s) => ratio(s.pollVotesGivenCount, 5),
  },
  inviteFriend: {
    code: 'inviteFriend',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Invite a friend who joins',
    adminMeasurement: "reputation_ledger_entries with reason 'referral', bar of 1, since joining",
    label: (s, ctx) => `Invite an artist friend — ${tally(s.referralCount, 1, ctx)} joined`,
    metric: 'referralCount',
    bar: 1,
    evidence: 'An invite that lands is worth points to you and a head start to them.',
    why: 'This place is more useful with someone in it you already know. Invite an artist friend, and when they join you both start with points — and you have someone whose work you actually want to see.',
    minutes: 2,
    href: '/quests',
    opens: 'invite',
    progress: (s) => ratio(s.referralCount, 1),
  },
  inviteThreeFriends: {
    code: 'inviteThreeFriends',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Invite three friends who join',
    adminMeasurement: "reputation_ledger_entries with reason 'referral', bar of 3, since joining",
    label: (s, ctx) => `Invite three artist friends — ${tally(s.referralCount, 3, ctx)} joined`,
    metric: 'referralCount',
    bar: 3,
    evidence: 'The biggest single thing you can earn here, and it pays every time one of them arrives.',
    why: 'Everything else on this list earns you a few points at a time. A friend who actually joins pays you more than the rest of the quest put together, and they arrive already knowing somebody here, which is the thing most people never get.',
    minutes: 4,
    href: '/quests',
    opens: 'invite',
    progress: (s) => ratio(s.referralCount, 3),
  },
  answerRecentCritique: {
    code: 'answerRecentCritique',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Answer a live critique request (1)',
    adminMeasurement: 'community_critiques authored on other artists posts, bar of 1, since joining',
    label: (s, ctx) => `Answer somebody waiting for a critique — ${tally(s.critiquesGivenCount, 1, ctx)}`,
    metric: 'critiquesGivenCount',
    bar: 1,
    evidence:
      'Nobody has answered these yet, and the window closes. One reply is worth more points than anything else here.',
    why: 'Asking strangers to judge your work takes nerve, and being met with silence teaches somebody not to try again. Answering one properly is the most useful ten minutes you can spend here, and the artist can mark it as having helped, which pays you again.',
    minutes: 10,
    href: '/community/critiques',
    opens: 'critique-answer',
    // Bar of ONE is what makes this the fast-points step; critiquesGiven (bar 5) is the
    // grind version. Never gated on valuedAt — the recipient sets that, and a step she
    // cannot finish alone is the trap the critique-blitz seed already warns about.
    progress: (s) => ratio(s.critiquesGivenCount, 1),
  },
  /**
   * RETAINED ALIAS of `reviveLapsedLike`. Live quests carry this code and a quest silently
   * losing a step re-weights it, so the code stays — but the MEASUREMENT is now the honest
   * one.
   */
  reviveLapsed: {
    code: 'reviveLapsed',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Bring someone back (5 lapsed artists)',
    adminMeasurement:
      'distinct lapsed artists from the picker whose artwork she liked or reacted to, bar of 5, since joining',
    label: (s, ctx) => `Bring someone back — ${tally(s.reviveLikeCount, 5, ctx)}`,
    metric: 'reviveLikeCount',
    bar: 5,
    evidence: 'These artists have not been here in weeks. A notification from a person is what brings them back.',
    why: 'People stop coming because it went quiet, not because they stopped making things. A reaction on work they posted a month ago arrives with your name on it, and that is the difference between a dead account and somebody who opens the app again tonight.',
    minutes: 3,
    href: '/artists',
    opens: 'dormant-targets',
    // This USED to count any like on any artist, deliberately: "likes given to people who
    // are dormant" would un-count itself the moment a nudge worked and flipped their
    // last-seen stamp, and a measured step cannot be ticked by hand to fix that. The cost
    // was a step whose copy said "bring someone back" while its measurement accepted a
    // like on anybody. `quest_target_serves` dissolves the trade: a serve row is durable
    // proof we named them lapsed at that instant, so the count is both honest and stable.
    progress: (s) => ratio(s.reviveLikeCount, 5),
  },
  reviveLapsedLike: {
    code: 'reviveLapsedLike',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Bring 5 lapsed artists back (like their work)',
    adminMeasurement:
      'distinct lapsed artists from the picker whose artwork she liked or reacted to, bar of 5, since joining',
    label: (s, ctx) => `Bring someone back — ${tally(s.reviveLikeCount, 5, ctx)}`,
    metric: 'reviveLikeCount',
    bar: 5,
    evidence: 'These artists have not been here in weeks. A notification from a person is what brings them back.',
    why: 'People stop coming because it went quiet, not because they stopped making things. A reaction on work they posted a month ago arrives with your name on it, and that is the difference between a dead account and somebody who opens the app again tonight.',
    minutes: 3,
    href: '/artists',
    opens: 'dormant-targets',
    progress: (s) => ratio(s.reviveLikeCount, 5),
  },
  reviveLapsedComment: {
    code: 'reviveLapsedComment',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: "Comment on 3 lapsed artists' work",
    adminMeasurement: 'distinct lapsed artists from the picker whose artwork she commented on, bar of 3, since joining',
    label: (s, ctx) => `Comment on their work — ${tally(s.reviveCommentCount, 3, ctx)}`,
    metric: 'reviveCommentCount',
    bar: 3,
    evidence: 'A reaction says someone looked. A sentence says someone looked properly.',
    why: 'They have been gone long enough to assume nobody noticed they left. A line about the actual piece — what you saw in it — is harder to dismiss than a notification count, and it is the one thing that tells them this place still has people in it.',
    minutes: 5,
    href: '/artists',
    opens: 'dormant-targets',
    progress: (s) => ratio(s.reviveCommentCount, 3),
  },
  reviveLapsedFollow: {
    code: 'reviveLapsedFollow',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Follow 5 lapsed artists',
    adminMeasurement: 'distinct lapsed artists from the picker she followed, bar of 5, since joining',
    label: (s, ctx) => `Follow a few of them — ${tally(s.reviveFollowCount, 5, ctx)}`,
    metric: 'reviveFollowCount',
    bar: 5,
    evidence: 'A new follower is a reason to come back and see who it was.',
    why: 'A follow costs you a tap and gives them a number that moved while they were away. It also means their next piece has somewhere to land, which is what stops them drifting off again a week after they return.',
    minutes: 2,
    href: '/artists',
    opens: 'dormant-targets',
    progress: (s) => ratio(s.reviveFollowCount, 5),
  },
  reviveLapsedConnect: {
    code: 'reviveLapsedConnect',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Connect with 3 lapsed artists',
    adminMeasurement:
      'distinct lapsed artists from the picker she sent a connection request to with no note, bar of 3, since joining',
    label: (s, ctx) => `Ask to connect — ${tally(s.reviveConnectCount, 3, ctx)}`,
    metric: 'reviveConnectCount',
    bar: 3,
    evidence: 'A connection is the only one of these that lets them write back.',
    why: 'Everything else here is one-way. A connection opens a door, and your open door is what somebody deciding whether this place is worth coming back to actually needs to find.',
    minutes: 3,
    href: '/artists',
    opens: 'dormant-targets',
    progress: (s) => ratio(s.reviveConnectCount, 3),
  },
  reviveLapsedNote: {
    code: 'reviveLapsedNote',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Connect with a note to 3 lapsed artists',
    adminMeasurement:
      'distinct lapsed artists from the picker she wrote to, by connection note or DM, bar of 3, since joining',
    // NAMES THE CONTROL, not the outcome (Nick, 2026-09-26): "write to them" read like
    // the one-tap praise chips on the same card, which post a COMMENT and complete a
    // different step. A DM needs an accepted connection and this pool is strangers, so
    // the note travels on the connection request — say that.
    label: (s, ctx) => `Connect with a note — ${tally(s.reviveNoteCount, 3, ctx)}`,
    metric: 'reviveNoteCount',
    bar: 3,
    evidence: 'A connection is the only one of these that lets them answer you.',
    why: 'Connecting opens a door they can walk back through — it is the only one of these that lets them write to you. Sending it with a line about their actual piece is what makes it read as a person who looked, rather than a request from a stranger.',
    minutes: 5,
    href: '/artists',
    opens: 'dormant-note',
    progress: (s) => ratio(s.reviveNoteCount, 3),
  },
  greetWelcome: {
    code: 'greetWelcome',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Say hi to a new member (3)',
    adminMeasurement:
      'post_welcomes rows written by this member for someone who finished the onboarding wizard, bar of 3, since joining',
    label: (s, ctx) => `Say hi to a new member — ${tally(s.greetWelcomeCount, 3, ctx)}`,
    metric: 'greetWelcomeCount',
    bar: 3,
    evidence: 'One tap. They get a notification with your name on it, and a way to answer you.',
    why: 'Nobody arrives knowing anyone, and silence in the first week is what teaches somebody that this place is not for them. Saying hi costs you a second and is the whole difference between their first post landing and disappearing.',
    minutes: 2,
    href: '/community/new-members',
    opens: 'greet-targets',
    progress: (s) => ratio(s.greetWelcomeCount, 3),
  },
  greetReact: {
    code: 'greetReact',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: "React to a newcomer's work (5)",
    adminMeasurement: 'reactions on artwork by members new at the time, bar of 5, since joining',
    label: (s, ctx) => `React to a newcomer's work — ${tally(s.greetReactCount, 5, ctx)}`,
    metric: 'greetReactCount',
    bar: 5,
    evidence: 'A reaction takes one tap and lands as a notification on someone who has had none.',
    why: 'The first days decide whether someone stays. A single emoji on their work is the cheapest thing you can do that tells a stranger their art was seen by a person.',
    minutes: 2,
    href: '/artists',
    opens: 'greet-targets',
    progress: (s) => ratio(s.greetReactCount, 5),
  },
  greetArtworkComment: {
    code: 'greetArtworkComment',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: "Comment on a newcomer's artwork (3)",
    adminMeasurement:
      'distinct newcomer artists commented on (image comments on artwork by members new at the time; several comments on one artist count once), bar of 3, since joining',
    label: (s, ctx) => `Say something about a newcomer's piece — ${tally(s.greetArtworkCommentCount, 3, ctx)}`,
    metric: 'greetArtworkCommentCount',
    bar: 3,
    evidence: 'A comment is the first thing a new artist screenshots.',
    why: 'A reaction says someone looked; a sentence says you looked properly. For an artist in their first week that is the difference between posting into a room and being answered in one, and yours might be the only answer they get.',
    minutes: 4,
    href: '/community',
    opens: 'greet-targets',
    progress: (s) => ratio(s.greetArtworkCommentCount, 3),
  },
  greetPostComment: {
    code: 'greetPostComment',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: "Reply to a newcomer's post (3)",
    adminMeasurement: 'community comments on posts by members new at the time, bar of 3, since joining',
    label: (s, ctx) => `Reply to a newcomer's post — ${tally(s.greetPostCommentCount, 3, ctx)}`,
    metric: 'greetPostCommentCount',
    bar: 3,
    evidence: 'An unanswered first post is the most common reason someone never posts again.',
    why: 'Introducing yourself to a room that says nothing back is a bad first day. Answering one costs you a minute and changes what this place is for the person who wrote it.',
    minutes: 3,
    href: '/community/new-members',
    opens: 'greet-targets',
    progress: (s) => ratio(s.greetPostCommentCount, 3),
  },
  greetFollow: {
    code: 'greetFollow',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Follow a newcomer (5)',
    adminMeasurement: 'follows of members new at the time, bar of 5, since joining',
    label: (s, ctx) => `Follow a few newcomers — ${tally(s.greetFollowCount, 5, ctx)}`,
    metric: 'greetFollowCount',
    bar: 5,
    evidence: 'A follower notification is the first number that moves for a new artist.',
    why: 'Going from zero followers to one is the biggest proportional jump anybody here ever gets. It costs you a tap and it is the number they will check again tomorrow.',
    minutes: 2,
    href: '/artists',
    opens: 'artist-matches',
    progress: (s) => ratio(s.greetFollowCount, 5),
  },
  greetConnect: {
    code: 'greetConnect',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'Connect with a newcomer (3)',
    adminMeasurement: 'accepted connections with members new at the time, bar of 3, since joining',
    label: (s, ctx) => `Connect with a newcomer — ${tally(s.greetConnectCount, 3, ctx)}`,
    metric: 'greetConnectCount',
    bar: 3,
    evidence: 'A connection is the only one of these that lets them write back.',
    why: 'Everything else here is one-way. A connection opens a door, and for somebody who arrived last week knowing nobody, your open door is what makes the difference between a site and a community.',
    minutes: 3,
    href: '/artists',
    opens: 'artist-matches',
    progress: (s) => ratio(s.greetConnectCount, 3),
  },
  greetCritique: {
    code: 'greetCritique',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: "Critique a newcomer's piece (1)",
    adminMeasurement: 'critiques written on posts by members new at the time, bar of 1, since joining',
    label: (s, ctx) => `Critique a newcomer's piece — ${tally(s.greetCritiqueCount, 1, ctx)}`,
    metric: 'greetCritiqueCount',
    bar: 1,
    evidence: 'A critique request with no answers is worse than never asking.',
    why: 'Asking strangers to judge your work takes nerve, and being met with silence teaches somebody not to try again. Answering one newcomer properly is the most useful twenty minutes you can spend here.',
    minutes: 10,
    href: '/community/critiques',
    opens: 'critique-answer',
    progress: (s) => ratio(s.greetCritiqueCount, 1),
  },
  greetFollowerReact: {
    code: 'greetFollowerReact',
    completion: 'measured',
    scope: 'since-join',
    adminLabel: 'React to new artists who followed you (3)',
    adminMeasurement:
      'distinct newcomer artists who followed this member, whose artwork she then liked or reacted to (newcomer at the time of the reaction; several pieces by one artist count once), bar of 3, since joining',
    label: (s, ctx) => `React to new artists who followed you — ${tally(s.greetFollowerReactCount, 3, ctx)}`,
    metric: 'greetFollowerReactCount',
    bar: 3,
    evidence: 'They followed you first. A like back is the notification that brings them back tomorrow.',
    why: 'A new artist who follows you is waiting to see whether anyone here notices them. Liking one of their pieces takes a tap, and it is the moment their follow turns into a conversation instead of a number.',
    minutes: 2,
    href: '/notifications',
    opens: 'greet-targets',
    progress: (s) => ratio(s.greetFollowerReactCount, 3),
  },
  /**
   * Never offered in the catalog picker as something to "add" on its own — the admin UI
   * creates `custom:N` rows that resolve here. Its defaults exist so a half-filled custom
   * step still renders a sentence rather than an empty row.
   */
  custom: {
    code: 'custom',
    completion: 'click',
    scope: 'lifetime',
    adminLabel: 'Custom step',
    adminMeasurement: 'nothing by default — completes on click, or borrows a step via `measures`',
    // These defaults are what a MEMBER reads if the admin leaves a box empty, so they are
    // written to her, not to whoever is configuring the quest. The copy rules in
    // score.spec.ts hold them to the same bar as every other step — which is how this
    // entry got rewritten once already.
    label: () => 'One more thing worth doing',
    evidence: 'This one was picked for this quest specifically.',
    why: 'This step was added to this quest on purpose, because it is worth your time alongside the rest of it. Whoever set the quest up can say exactly what it asks of you and where it takes you.',
    href: '/community',
    opens: 'embed',
    progress: () => 0,
  },
  collections: {
    code: 'collections',
    completion: 'measured',
    scope: 'lifetime',
    adminLabel: 'Group work into collections (3)',
    adminMeasurement: 'collections created, bar of 3',
    label: (s, ctx) => `Group your work into collections — ${tally(s.collectionCount, 3, ctx)}`,
    metric: 'collectionCount',
    bar: 3,
    evidence: 'A body of work reads as a body of work when it is grouped. A flat grid reads as a camera roll.',
    why: 'Collections are how you show a series as a series, and how you send one coherent group to a gallery or a client instead of everything you have ever made.',
    minutes: 5,
    href: '/my-artwork',
    opens: 'embed',
    progress: (s) => ratio(s.collectionCount, 3),
  },
};

export type QuestStepCode = keyof typeof QUEST_STEP_CATALOG;
